import { Home, HomeCategory, FilterOptions } from '../types';
import homesData from '../data/homes.json';

const homes: Home[] = homesData as Home[];

export const getAllHomes = (): Home[] => {
  return homes;
};

export const getHomesByCategory = (category: HomeCategory): Home[] => {
  return homes.filter(home => home.category === category);
};

export const getHomeBySlug = (slug: string, category?: HomeCategory): Home | undefined => {
  if (category) {
    return homes.find(home => home.slug === slug && home.category === category);
  }
  return homes.find(home => home.slug === slug);
};

export const getFeaturedHomes = (): Home[] => {
  return homes.filter(home => home.isFeatured);
};

export const getRelatedHomes = (homeId: string, category: HomeCategory, limit: number = 3): Home[] => {
  const currentHome = homes.find(home => home.id === homeId);
  if (!currentHome) return [];

  // First try to get homes from related IDs
  let related: Home[] = [];
  if (currentHome.relatedHomes) {
    related = homes.filter(home => 
      currentHome.relatedHomes!.includes(home.id) && home.id !== homeId
    );
  }

  // If we need more homes, get others from the same category
  if (related.length < limit) {
    const categoryHomes = homes.filter(home => 
      home.category === category && 
      home.id !== homeId && 
      !related.some(r => r.id === home.id)
    );
    related = [...related, ...categoryHomes];
  }

  return related.slice(0, limit);
};

export const filterHomes = (filters: FilterOptions): Home[] => {
  return homes.filter(home => {
    if (filters.category && filters.category !== 'all' && home.category !== filters.category) {
      return false;
    }
    
    if (filters.minBedrooms && home.specs.bedrooms < filters.minBedrooms) {
      return false;
    }
    
    if (filters.maxBedrooms && home.specs.bedrooms > filters.maxBedrooms) {
      return false;
    }
    
    if (filters.minBathrooms && home.specs.bathrooms < filters.minBathrooms) {
      return false;
    }
    
    if (filters.maxBathrooms && home.specs.bathrooms > filters.maxBathrooms) {
      return false;
    }
    
    if (filters.minArea && home.specs.totalArea < filters.minArea) {
      return false;
    }
    
    if (filters.maxArea && home.specs.totalArea > filters.maxArea) {
      return false;
    }
    
    return true;
  });
};

export const searchHomes = (query: string): Home[] => {
  const lowercaseQuery = query.toLowerCase();
  return homes.filter(home => 
    home.name.toLowerCase().includes(lowercaseQuery) ||
    getHomeDisplayName(home).toLowerCase().includes(lowercaseQuery) ||
    home.keyFeatures.some(feature => feature.toLowerCase().includes(lowercaseQuery)) ||
    home.category.toLowerCase().includes(lowercaseQuery)
  );
};

export const getCategoryDisplayName = (category: HomeCategory): string => {
  switch (category) {
    case 'core':
      return 'Core Homes';
    case 'family':
      return 'Family Homes';
    case 'kiwiana':
      return 'Kiwiana Homes';
    default:
      return category;
  }
};

export const getHomeDisplayName = (home: Home): string => {
  const categoryName = getCategoryDisplayName(home.category);
  // Extract the number from the plan name (e.g., "Plan01" -> "01")
  const planNumber = home.name.replace(/^Plan/i, '');
  // Remove "Homes" from category name and add "Home" + number
  const baseName = categoryName.replace(' Homes', ' Home');
  return `${baseName} ${planNumber}`;
};